import React, { useState, useEffect, useMemo } from 'react';
import HolsterVisualizer from '../HolsterVisualizer';
import ColorSelector from '../ColorSelector';
import OptionSelector from '../OptionSelector';
import SegmentedControl from '../SegmentedControl';
import useConfigShare from '../useConfigShare';
import ShareModal from '../ShareModal';

// --- IMPORTS FOR REUSABLE COMPONENTS ---
import DisclaimerBox from '../DisclaimerBox'; 
import CollapsibleSection from '../CollapsibleSection';

// 1. Import Global Settings (UPDATED WITH LEAD TIME CONSTANTS)
import { 
  ENABLE_RUSH_SHIPPING, 
  GLOBAL_DISCOUNT_PERCENT, 
  SALE_NAME,
  QS_DAYS, 
  RUSH_DAYS, 
  CUSTOM_DAYS,  
  QS_TIMELINE_TEXT, 
  RUSH_TIMELINE_TEXT, 
  CUSTOM_TIMELINE_TEXT,
  // Lead time labels + colors
  QS_LABEL, CUSTOM_LABEL, QS_COLOR, CUSTOM_COLOR, RUSH_COLOR, leadTimeTint,
} from '../config/globalSettings';

// 2. Import Data
import { magData } from '../data/magData'; 
import '../App.css';

function MagCarrierBuilder() {
  // --- CUSTOM OVERRIDES FOR MAG CARRIER ---
  const MAG_RUSH_PRICE = 15.00; // Hardcoded $15 rush fee specifically for Mags

  // --- STATE ---
  const [viewSide, setViewSide] = useState("front"); 
  const [magId, setMagId] = useState("");
  const [configId, setConfigId] = useState(""); 
  const [depthId, setDepthId] = useState(""); 
  const [colorId, setColorId] = useState("");
  const [mountId, setMountId] = useState("");
  const [rushService, setRushService] = useState(false);

  // --- SHARE FUNCTIONALITY ---
  const {
    getConfigFromURL, generateShareableURL, saveToLocalStorage, loadFromLocalStorage,
    savedConfigs, saveNamedConfig, deleteNamedConfig, copyToClipboard, copySuccess,
    shareModalOpen, setShareModalOpen,
  } = useConfigShare();

  const getCurrentConfig = () => ({
    magId, configId, depthId, colorId, mountId, rushService
  });

  const loadConfig = (config) => {
    if (!config) return;
    setMagId(config.magId || '');
    setConfigId(config.configId || '');
    setDepthId(config.depthId || '');
    setColorId(config.colorId || '');
    setMountId(config.mountId || '');
    setRushService(config.rushService || false);
  };

  useEffect(() => {
    const urlConfig = getConfigFromURL();
    if (urlConfig) {
      loadConfig(urlConfig);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    const savedConfig = loadFromLocalStorage();
    if (savedConfig) { loadConfig(savedConfig); }
  }, []);

  useEffect(() => {
    if (magId) saveToLocalStorage(getCurrentConfig());
  }, [magId, configId, depthId, colorId, mountId, rushService]);

  const shareableURL = generateShareableURL(getCurrentConfig());

  // --- SELECTIONS ---
  const selectedMag = magData.mags.find(m => m.id === magId);
  const selectedConfig = magData.configurations.find(c => c.id === configId);
  const selectedColor = magData.colors.find(c => c.id === colorId);
  const selectedMount = magData.mounts.find(m => m.id === mountId);

  // --- STABLE REFERENCES FOR VISUALIZER ---
  const gunDataObj = useMemo(() => {
      return { name: selectedMag ? selectedMag.name : "Mag Carrier" };
  }, [selectedMag?.name]);

  // --- DEPTH FILTERING LOGIC ---
  const depthProfile = selectedMag?.depth_profile || "standard_only";
  const showDepthDropdown = depthProfile !== "standard_only";

  const getFilteredDepthOptions = () => {
      if (depthProfile === "staccato") {
          return magData.depth_options.filter(d => ["standard", "standard_adj", "deep_adj"].includes(d.id));
      }
      if (depthProfile === "all") {
          return magData.depth_options.filter(d => ["standard", "deep", "deep_adj"].includes(d.id));
      }
      return magData.depth_options.filter(d => d.id === "standard"); // Fallback
  };

  const filteredDepthOptions = getFilteredDepthOptions();
  const selectedDepth = magData.depth_options?.find(d => d.id === depthId);

  // Auto-reset depth if it becomes invalid when switching mags
  useEffect(() => {
      if (!showDepthDropdown) {
          setDepthId("standard"); // Auto lock it behind the scenes
      } else if (depthId && !filteredDepthOptions.find(o => o.id === depthId)) {
          setDepthId(""); // Reset if current choice is no longer valid
      }
  }, [magId, depthProfile, depthId, filteredDepthOptions, showDepthDropdown]);


  // --- LEAD TIME LOGIC (UPDATED TO USE GLOBALS) ---
  const calculateLeadTime = () => {
    if (!selectedMag) return { text: "Select Options", date: "...", type: "PENDING" };

    let isQuickShip = false;
    if (selectedMag.qs_mag) isQuickShip = true;

    // Use Global Constants
    const qsDays = QS_DAYS;       
    const rushDays = RUSH_DAYS;    
    const customDays = CUSTOM_DAYS;  
    
    // Separate the logic so Rush gets its own timeline
    let targetDays = customDays;
    if (isQuickShip) {
        targetDays = qsDays;
    } else if (rushService) {
        targetDays = rushDays;
    }
    
    const date = new Date();
    let daysAdded = 0;
    while (daysAdded < targetDays) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) daysAdded++;
    }
    const dateString = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    if (isQuickShip) {
        return { type: QS_LABEL, timeline: QS_TIMELINE_TEXT, date: dateString, color: QS_COLOR, bg: leadTimeTint(QS_COLOR), isCustom: false };
    } 
    if (rushService && ENABLE_RUSH_SHIPPING) {
        return { type: "RUSH PRIORITY", timeline: RUSH_TIMELINE_TEXT, date: dateString, color: RUSH_COLOR, bg: leadTimeTint(RUSH_COLOR), isCustom: true };
    }
    return { type: CUSTOM_LABEL, timeline: CUSTOM_TIMELINE_TEXT, date: dateString, color: CUSTOM_COLOR, bg: leadTimeTint(CUSTOM_COLOR), isCustom: true };
  };

  const leadTimeInfo = calculateLeadTime();

  // --- AUTO-CLEAR RUSH WHEN NOT APPLICABLE ---
  useEffect(() => {
    if (rushService && (!leadTimeInfo.isCustom || leadTimeInfo.type === QS_LABEL)) {
        setRushService(false);
    }
  }, [magId]);

  // --- PRICING ---
  const BASE_PRICE = 29.99;
  
  const subtotal = 
    BASE_PRICE +
    (selectedColor?.price || 0) +
    (selectedMount?.price || 0) +
    (selectedDepth?.price || 0) + 
    (rushService && ENABLE_RUSH_SHIPPING ? MAG_RUSH_PRICE : 0);

  const discountAmount = subtotal * GLOBAL_DISCOUNT_PERCENT;
  const total = (subtotal - discountAmount).toFixed(2);

  // Validation
  const isReady = magId && configId && colorId && mountId &&
                  (!showDepthDropdown || (showDepthDropdown && depthId));

  // --- ADD TO CART (UPDATED CART TEXT) ---
  const handleAddToCart = () => {
    if (!isReady) return;

    // Use the actual selected depth, or default to standard if dropdown is hidden
    let finalDepthName = "Standard Depth";
    if (showDepthDropdown && selectedDepth) {
        finalDepthName = selectedDepth.name;
    }

    const configuration = [
        `Mag Model: ${selectedMag?.name}`,
        `Configuration: ${selectedConfig?.name}`,
        `Concealment Depth: ${finalDepthName}`,
        `Color: ${selectedColor?.name}`,
        `Mount: ${selectedMount?.name}`,
        `Warranty: Free Lifetime Warranty`,
        `Lead Time: ${leadTimeInfo.timeline}`,
    ].filter(Boolean).join('\n');

    const productID = 283908; // UPDATED LIVE PISTOL MAG ID
    
    const params = new URLSearchParams();
    params.append('add-to-cart', productID);
    params.append('quantity', '1');
    params.append('anr_config', configuration);
    params.append('anr_price', total);

    // REDIRECT TO WOOCOMMERCE CART
    window.location.href = `/checkout/?${params.toString()}`;
  };

  // --- REUSABLE HELPER FOR HEADERS WITH INFO (H3 Level) ---
  const SectionHeader = ({ title, description }) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px" }}>
        <h3 style={{ textTransform: "uppercase", margin: 0 }}>{title}</h3>
        {description && (
            <div className="tooltip-wrapper">
                <span className="info-icon">i</span>
                <div className="tooltip-content">{description}</div>
            </div>
        )}
    </div>
  );

  // --- HELPER COMPONENT: Label with Info Tooltip (Input Level) ---
  const LabelWithTooltip = ({ title, description, imageUrl }) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
        <label style={{ margin: 0, fontSize: "14px", fontWeight: "bold", color: "#fff", textTransform: "uppercase" }}>{title}</label>
        {(description || imageUrl) && (
            <div className="tooltip-wrapper">
                <span className="info-icon">i</span>
                <div className="tooltip-content">
                    {imageUrl && (
                        <div style={{marginBottom: "10px"}}>
                            <img src={imageUrl} alt="Info" style={{width: "100%", borderRadius: "4px"}} />
                        </div>
                    )}
                    {description}
                </div>
            </div>
        )}
    </div>
  );

  return (
    <div className={`builder-container ${colorId ? "sticky-active" : ""}`}>
      <div className="controls-section">
        <h1 className="header-title">Pistol Mag Builder <br/><span style={{fontSize:"16px", color:"#D5A678"}}>CUSTOM MAGAZINE CARRIERS</span></h1>
        
        {/* FEATURES */}
        <div style={{ color: "#aaa", fontSize: "14px", lineHeight: "1.7", marginBottom: "40px" }}>
           <h3 style={{ border: "none", padding: 0, color: "#fff", textTransform: "uppercase" }}>Features</h3>
           <ul style={{ paddingLeft: "20px", marginBottom: "20px", listStyleType: "disc" }}>
             <li><strong style={{ color: "#fff" }}>Unwavering Retention:</strong> Keep your magazines locked in place during dynamic movements.</li>
             <li><strong style={{ color: "#fff" }}>Lightning-Fast Reloads:</strong> Experience seamless mag changes with our open-top design.</li>
             <li><strong style={{ color: "#fff" }}>Versatile Carry Options:</strong> Tailor your carry style with a variety of mounting options.</li>
             <li><strong style={{ color: "#fff" }}>Built to Last:</strong> Crafted from durable KYDEX® Thermoplastics.</li>
             <li><strong style={{ color: "#fff" }}>Wide Range of Models:</strong> Find the perfect fit for your pistol magazines.</li>
           </ul>
        </div>

        {/* MAG SELECTION */}
        <OptionSelector label="Select Magazine Model *" options={magData.mags} selectedValue={magId} onChange={setMagId} />
        
        {/* CONFIGURATION - DROPDOWN */}
        <OptionSelector label="Carry Configuration *" options={magData.configurations} selectedValue={configId} onChange={setConfigId} />

        {/* DYNAMIC DEPTH SELECTION */}
        {showDepthDropdown && (
            <div className="fade-in-section" style={{marginTop: "15px"}}>
                <LabelWithTooltip 
                    title="Concealment Depth *" 
                    description="For battle belt use, we suggest our Deep magazine carrier (NOT ADJUSTABLE). Not only is this option ideal in supporting hard use, it also allows the user to have a deeper sitting magazines on the battle belt so that longer extended magazine don't crash into chest rigs and plate carriers."
                    imageUrl="/wp-content/uploads/anr-assets/images/info/mag_depth.jpg"
                />
                <OptionSelector label="" options={filteredDepthOptions} selectedValue={depthId} onChange={setDepthId} />
            </div>
        )}

        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>
        
        {/* COLORS WITH TOOLTIP */}
        <SectionHeader 
            title="Color Options" 
            description="ANR Design does not have control over the pattern. The model portrayed in the configurator is a representation." 
        />
        <ColorSelector options={magData.colors} selectedValue={colorId} onChange={setColorId} />
        
        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>

        {/* MOUNTING WITH MEGA TOOLTIP */}
        <SectionHeader 
            title="Mounting Options" 
            description={
                <div className="mega-tooltip">
                    {magData.mounts.map(mount => (
                        <div key={mount.id} className="mount-info-row" style={{marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #555'}}>
                            <div className="mount-text">
                                <h4 style={{marginTop: 0, marginBottom: '5px'}}>{mount.name}</h4>
                                <p style={{margin: 0}}>{mount.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
            } 
        />
        <OptionSelector label="Select Mount *" options={magData.mounts} selectedValue={mountId} onChange={setMountId} />

        {/* --- FREE LIFETIME WARRANTY BANNER --- */}
        <div style={{ marginTop: "30px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                <h3 style={{ textTransform: "uppercase", margin: 0 }}>Lifetime Warranty</h3>
                <div className="tooltip-wrapper">
                    <span className="info-icon">i</span>
                    <div className="tooltip-content">
                        Every ANR Design holster is backed by an automatic Lifetime Warranty on the Kydex® shell for the original purchaser. If your shell cracks or breaks under normal, intended use, we replace it. Consumables (hardware, loops, spacers), cosmetic wear, and damage from modifications or extreme heat are not covered. Return shipping fees apply.
                        <br/><br/>
                        <a href="https://www.anrkydexholsters.com/warranty-privacy-and-shipping-policy/" target="_blank" rel="noopener noreferrer" style={{ color: "#D5A678", textDecoration: "underline", fontWeight: "bold" }}>
                            Learn More & File a Claim
                        </a>
                    </div>
                </div>
            </div>
            <div style={{
                padding: "15px",
                backgroundColor: "rgba(76, 175, 80, 0.1)",
                borderLeft: "4px solid #4caf50",
                borderRadius: "4px",
                fontSize: "14px",
                color: "#4caf50",
                fontWeight: "600"
            }}>
                ✓ Free Lifetime Warranty Included
            </div>
        </div>

        <hr style={{margin:"50px 0 20px 0", borderTop:"1px solid #333"}}/>

        {/* --- COLLAPSIBLE DESCRIPTION --- */}
        <CollapsibleSection title="Description">
            <p style={{ marginTop: 0 }}>
                We understand how important it is to have those extra rounds on deck and our single KYDEX® Thermoplastics pistol mag carriers provide you with that peace of mind. 
                These carriers are molded with increased durability and perfect retention needed for all applications. Made with the highest quality materials available, our 
                KYDEX® Thermoplastics magazine carriers are built for hard use, standing up to the punishment of our sponsored competition shooters and everyday users. We offer 
                the most popular selection of magazines available and rest assured these carriers are produced using the same methods as our KYDEX® Thermoplastics holsters 
                insuring each carrier is tested and retested before leaving the shop.
            </p>
        </CollapsibleSection>

        {/* COLLAPSIBLE WARNINGS */}
        <div className="mobile-only-content">
            <CollapsibleSection title="Important Notes & Warnings">
                <DisclaimerBox mode="mobile" setShareModalOpen={setShareModalOpen} gunId={magId} />
            </CollapsibleSection>
        </div>

      </div>

      <div className="preview-section">
        <div className="sticky-wrapper">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
            
            {/* Visualizer Disclaimer Note */}
            <div style={{ color: "#D5A678", fontSize: "11px", fontWeight: "bold", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                * Visualizer represents <br/>Deep Adjustable model
            </div>

          </div>

          <HolsterVisualizer 
            holsterType="mag" 
            gunData={gunDataObj} 
            colorData={selectedColor} 
            mountData={selectedMount} 
            isLightbearing="no" 
            viewSide={viewSide} 
          />

          {/* --- FRONT / REAR TOGGLE --- */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "15px", marginBottom: "15px" }}>
            <div style={{ display: "flex", backgroundColor: "#333", borderRadius: "4px", padding: "4px", gap: "5px" }}>
              <button onClick={() => setViewSide("front")} style={{ background: viewSide === "front" ? "#D5A678" : "transparent", color: viewSide === "front" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>FRONT</button>
              <button onClick={() => setViewSide("back")} style={{ background: viewSide === "back" ? "#D5A678" : "transparent", color: viewSide === "back" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>REAR</button>
            </div>
          </div>

          {/* --- LEAD TIME BOX --- */}
          {leadTimeInfo.type !== "PENDING" && (
             <div className="lead-time-box" style={{ 
                 backgroundColor: leadTimeInfo.bg, 
                 padding: "15px", 
                 borderLeft: `4px solid ${leadTimeInfo.color}`, 
                 marginBottom: "20px"
             }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                    <div>
                        {leadTimeInfo.type && <strong style={{ color: leadTimeInfo.color, textTransform: "uppercase", fontSize: "16px" }}>{leadTimeInfo.type}</strong>}
                        <div style={{ marginTop: "5px", color: "#fff" }}>
                        Lead Time: <strong style={{ color: leadTimeInfo.color }}>{leadTimeInfo.timeline}</strong><br/>
                        Est. Ship Date: <strong>{leadTimeInfo.date}</strong>
                        </div>
                    </div>
                    
                    {/* RUSH TOGGLE */}
                    {leadTimeInfo.isCustom && ENABLE_RUSH_SHIPPING && (
                        <div style={{ marginLeft: "15px" }}>
                            <label style={{ 
                                display: "flex", alignItems: "center", cursor: "pointer",
                                background: rushService ? "#D5A678" : "transparent",
                                border: "1px solid #d32f2f", padding: "12px 20px", borderRadius: "4px",
                                transition: "all 0.2s"
                            }}>
                                <input 
                                    type="checkbox" 
                                    checked={rushService} 
                                    onChange={(e) => setRushService(e.target.checked)}
                                    style={{ marginRight: "8px", accentColor: "#000" }} 
                                />
                                <div style={{ lineHeight: "1.7" }}>
                                    <div style={{ fontSize: "15px", fontWeight: "bold", color: rushService ? "#000" : "#D5A678", textTransform: "uppercase" }}>RUSH MY BUILD</div>
                                    <div style={{ fontSize: "15px", color: rushService ? "#000" : "#ccc" }}>+${MAG_RUSH_PRICE.toFixed(2)}</div>
                                </div>
                            </label>
                        </div>
                    )}
                </div>
             </div>
          )}
          {leadTimeInfo.type === "PENDING" && (
            <div style={{ textAlign: "center", marginTop: "15px", color: "#666", fontSize: "12px", marginBottom: "15px" }}>Select options to see lead time</div>
          )}
          
          <div className="price-tag">
            {GLOBAL_DISCOUNT_PERCENT > 0 ? (
                <>
                <span style={{ textDecoration: "line-through", color: "#888", marginRight: "10px", fontSize: "0.8em" }}>${subtotal.toFixed(2)}</span>
                <span style={{ color: "#00ff00" }}>${total}</span>
                <div style={{ fontSize: "10px", color: "#00ff00", textTransform: "uppercase", letterSpacing: "1px", marginTop: "5px" }}>
                    {SALE_NAME} ACTIVE
                </div>
                </>
            ) : (
                `Total: $${total}`
            )}
          </div>

          {/* DESKTOP DISCLAIMERS */}
          <DisclaimerBox mode="desktop" setShareModalOpen={setShareModalOpen} gunId={magId} />

          <button className={`add-to-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>{isReady ? "ADD TO CART" : "SELECT OPTIONS"}</button>
        </div>
      </div>
      
      {/* MOBILE BAR */}
      <div className="mobile-action-bar mobile-only-content">
        <div className="mobile-price-container"><span className="mobile-label">Estimated Total</span><span className="mobile-total">${total}</span></div>
        <button className={`mobile-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>{isReady ? "ADD TO CART" : "INCOMPLETE"}</button>
      </div>

      <ShareModal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} shareableURL={shareableURL} currentConfig={getCurrentConfig()} savedConfigs={savedConfigs} onSaveConfig={saveNamedConfig} onLoadConfig={loadConfig} onDeleteConfig={deleteNamedConfig} onCopyLink={copyToClipboard} copySuccess={copySuccess} gunName={selectedMag?.name} colorName={selectedColor?.name} />
    </div>
  );
}

export default MagCarrierBuilder;